import React, { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';

import { useEditionAwareness } from '../../../services/edition-awareness-context';
import { getJiraApplicationKey } from '../../../services/get-jira-application-key';
import { getJiraProjectTypeKey } from '../../../services/get-jira-project-type-key';
import { isEligibleForTwcSkuRecommendation } from '../../../services/is-eligible-for-twc-sku-recommendation';
import { ModalEntryPoint } from '../shared/modal-entry-point';
import { DIALOG_CONFIG } from './dialog-config';
import { entryPoint, type EntryPointParams } from './freeToStandardModal.entrypoint';
import { buildTwcSkuRecommendationContext } from './twc-sku-recommendation';

export function FreeToStandardModal(): React.JSX.Element {
	const context = useEditionAwareness();
	const { product, cloudId, orgId } = context;
	const { locale } = useIntl();
	const isStorageLimitsExperimentEnabled: boolean =
		(product === 'jira-service-management' || product === 'jira-customer-service') &&
		FeatureGates.getExperimentValue<boolean>(
			'jsmgrowth-267_enforce_storage_limits_servco_free',
			'isEnabled',
			false,
		);
	const isTwcSkuRecommendationEnabled = isEligibleForTwcSkuRecommendation(context);
	const [recommendationSessionId, setRecommendationSessionId] = useState(() => crypto.randomUUID());

	const jiraApplicationKey = getJiraApplicationKey(product);
	const projectType = getJiraProjectTypeKey(product);

	const isJira = isStorageLimitsExperimentEnabled
		? product === 'jira' || product === 'jira-product-discovery'
		: product === 'jira' ||
			product === 'jira-product-discovery' ||
			product === 'jira-service-management';

	const entryPointParams: EntryPointParams = useMemo(
		() => ({
			cloudId,
			// Fallback to any valid value since this means that we're not using jira
			jiraApplicationKey: jiraApplicationKey ?? 'JIRA_SOFTWARE',
			// Fallback to any valid value since this means that we're not using jira
			projectType: projectType ?? 'SOFTWARE',
			isJira,
			isConfluence: product === 'confluence',
			isServiceCollectionFree:
				product === 'jira-service-management' && isStorageLimitsExperimentEnabled,
			isTwcSkuRecommendationEnabled,
			recommendationContext: isTwcSkuRecommendationEnabled
				? buildTwcSkuRecommendationContext({
						cloudId,
						orgId,
						product,
						locale,
						recommendationSessionId,
					})
				: null,
		}),
		[
			cloudId,
			orgId,
			product,
			projectType,
			jiraApplicationKey,
			isJira,
			isStorageLimitsExperimentEnabled,
			isTwcSkuRecommendationEnabled,
			locale,
			recommendationSessionId,
		],
	);

	const onClose = useCallback(() => {
		setRecommendationSessionId(crypto.randomUUID());
	}, []);

	const entryPointProps = useMemo(() => ({ onClose }), [onClose]);

	const closeAttributes = useMemo(
		() => (isTwcSkuRecommendationEnabled ? { recommendationSessionId } : undefined),
		[isTwcSkuRecommendationEnabled, recommendationSessionId],
	);

	const modalProps = useMemo(() => ({ width: 'x-large' }), []);

	return (
		<ModalEntryPoint
			entryPoint={entryPoint}
			entryPointParams={entryPointParams}
			entryPointProps={entryPointProps}
			closeAttributes={closeAttributes}
			modalProps={modalProps}
			dialogConfig={DIALOG_CONFIG}
		/>
	);
}
